import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const interests = [
  { value: "partner", label: "Partner with us" },
  { value: "community", label: "Bring it to my community" },
  { value: "donate", label: "Support / Donate" },
  { value: "volunteer", label: "Volunteer / Skills" },
  { value: "media", label: "Media enquiry" },
  { value: "general", label: "General question" },
];

const initial = {
  name: "",
  email: "",
  organisation: "",
  interest: "partner",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.organisation) delete payload.organisation;
      await axios.post(`${API}/contact`, payload);
      toast.success("Message sent. We'll be in touch soon.");
      setForm(initial);
      setSent(true);
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Could not send. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please review the form and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-sand py-24 md:py-36 overflow-hidden"
    >
      <div className="absolute inset-0 grain-overlay opacity-50" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20">
          {/* Left: heading + meta */}
          <div className="md:col-span-5">
            <div className="text-xs uppercase tracking-[0.3em] text-rust mb-6">
              Start the Conversation
            </div>
            <h2
              data-testid="contact-heading"
              className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight text-charcoal"
            >
              Tell us what you're
              <span className="block italic text-rust">building toward.</span>
            </h2>

            <p className="mt-8 text-base md:text-lg leading-relaxed text-ink">
              Every partnership begins with a conversation. Share a little about
              your work, your community, or your vision — and we'll write back
              personally.
            </p>

            <div className="mt-12 space-y-6">
              <div>
                <div className="text-[11px] uppercase tracking-[0.28em] text-ink/60 mb-2">
                  General
                </div>
                <a
                  href="mailto:hello@growingcountry.org"
                  className="font-serif text-2xl text-charcoal text-link"
                  data-testid="contact-email"
                >
                  hello@growingcountry.org
                </a>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.28em] text-ink/60 mb-2">
                  Based on Country
                </div>
                <div className="font-serif text-xl text-charcoal">
                  Alice Springs · Cairns · Darwin · Perth
                </div>
              </div>
            </div>
          </div>

          {/* Right: form */}
          <div className="md:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="bg-stone-alt border border-stone-line p-8 md:p-12 space-y-8"
            >
              {sent && (
                <div
                  data-testid="contact-success"
                  className="border border-sage bg-sage/10 px-5 py-4 text-sm text-sage-dark"
                >
                  Thanks — your message landed safely. We'll be in touch soon.
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <Field label="Your name" required>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={update("name")}
                    data-testid="contact-name-input"
                    className="w-full bg-transparent border-b border-stone-line focus:border-rust px-0 py-3 outline-none text-charcoal placeholder:text-ink/40 transition-colors"
                    placeholder="First and last"
                  />
                </Field>
                <Field label="Email" required>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={update("email")}
                    data-testid="contact-email-input"
                    className="w-full bg-transparent border-b border-stone-line focus:border-rust px-0 py-3 outline-none text-charcoal placeholder:text-ink/40 transition-colors"
                    placeholder="you@example.com"
                  />
                </Field>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <Field label="Organisation (optional)">
                  <input
                    type="text"
                    value={form.organisation}
                    onChange={update("organisation")}
                    data-testid="contact-organisation-input"
                    className="w-full bg-transparent border-b border-stone-line focus:border-rust px-0 py-3 outline-none text-charcoal placeholder:text-ink/40 transition-colors"
                    placeholder="Community, agency, company…"
                  />
                </Field>
                <Field label="I'm reaching out about" required>
                  <select
                    value={form.interest}
                    onChange={update("interest")}
                    data-testid="contact-interest-select"
                    className="w-full bg-transparent border-b border-stone-line focus:border-rust px-0 py-3 outline-none text-charcoal transition-colors"
                  >
                    {interests.map((i) => (
                      <option key={i.value} value={i.value}>
                        {i.label}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>

              <Field label="Your message" required>
                <textarea
                  required
                  rows={5}
                  value={form.message}
                  onChange={update("message")}
                  data-testid="contact-message-input"
                  className="w-full bg-transparent border-b border-stone-line focus:border-rust px-0 py-3 outline-none text-charcoal placeholder:text-ink/40 transition-colors resize-none"
                  placeholder="Tell us a little about what you have in mind…"
                />
              </Field>

              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-2">
                <p className="text-[11px] uppercase tracking-[0.2em] text-ink/60">
                  We respond within 5 business days
                </p>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit"
                  className="inline-flex items-center justify-center gap-2 bg-rust text-sand px-9 py-4 text-xs uppercase tracking-[0.25em] hover:bg-rust-dark transition-colors disabled:opacity-60"
                >
                  {loading ? "Sending…" : "Send message"}
                  <span aria-hidden>→</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-[0.28em] text-ink/70 mb-2">
        {label}
        {required && <span className="text-rust ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}
